import React, { useContext, useEffect, useState } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Dialog,
  Button,
  TextField,
  Typography,
  DialogContent,
  FormHelperText,
  Stack,
  Tooltip,
  Box,
} from "@mui/material";
// Imports from project
import "../../Style/dialogstyle.scss";
import EmptyImage from "../../Assets/empty-image.png";
import { AuthContext } from "../../Context/AuthContext";
import { DialogTitleComp } from "../../Utils/Component";
import useLessonForm from "./Hooks/useLessonForm";
import { SelectClassGroupFee } from "../Include/DynamicSelect/DynamicSelect";
import { useDeleteImageServer } from "../CropImage/DeleteImageServer";
import { UploadImageNoCrop } from "../CropImage/UploadImageNoCrop";
import LoadingProgess from "../../Utils/LoadingProgress";

export default function LessonForm({
  open,
  editData,
  setRefetch,
  dialogTitle,
  handleClose,
  // contentId,
  classId,
  hideBackdrop = false,
}) {
  const { t } = useContext(AuthContext);

  // =================================== Upload Image ====================================
  const mutationDelete = useDeleteImageServer();
  const [profileHook, setProfileHook] = useState("");
  const [imageFile, setImageFile] = useState(null);

  const [statusProgress, setStatusProgress] = useState(false);
  const [progress, setProgress] = useState(10);

  const handleUploadImage = async (e) => {
    const imageFile = e.target.files[0];
    setStatusProgress(true);
    if (imageFile) {
      setImageFile(imageFile);
      const uploadedImg = await UploadImageNoCrop(imageFile);
      setProfileHook(uploadedImg);
    }
  };

  const handleCloseAction = () => {
    handleClose();
    if (profileHook) {
      // delete iamge from server ====================================================
      let splitSrc = profileHook?.split(":")[4];
      mutationDelete.mutate({
        storage: "sr_music_academy",
        folder: "sr_music_images",
        file: splitSrc.split("/")[0],
      });
    }
    setProfileHook("");
    setImageFile("");
  };
  // ================================= End upload Image ===============================

  // ============================== Formik & Validation ===============================
  const CheckValidation = Yup.object().shape({
    title: Yup.string().required(t("required") || "Required"),
    groupFeeId: Yup.object().shape({
      id: Yup.string().required(t("required") || "Required"),
      title: Yup.string(),
    }),
    numbering: Yup.number().required(t("required") || "Required"),
    description: Yup.string(),
    videoLink: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      groupFeeId: { id: "", title: "" },
      numbering: 1,
      description: "",
      videoLink: "",
    },
    validationSchema: CheckValidation,
    onSubmit: async (values) => {
      setLoading(true);

      // console.log("InputLesson::", inputPayload);

      if (dialogTitle === "Create") {
        createLesson({
          variables: {
            input: {
              title: values.title,
              // contentId: contentId,
              classId: classId,
              groupFeeId: values?.groupFeeId?.id,
              numbering: values.numbering,
              description: values.description || "",
              videoLink: values.videoLink || "",
              image: profileHook,
            },
          },
        });
      } else {
        updateLesson({
          variables: {
            id: editData?._id,
            input: {
              title: values.title,
              // contentId: contentId,
              classId: classId,
              groupFeeId: values?.groupFeeId?.id,
              numbering: values.numbering,
              description: values.description || "",
              videoLink: values.videoLink || "",
              image: profileHook ? profileHook : editData?.image,
            },
          },
        });
      }
    },
  });

  const {
    errors,
    values,
    touched,
    resetForm,
    handleSubmit,
    setFieldValue,
    handleChange,
  } = formik;

  const { createLesson, loading, updateLesson, setLoading } = useLessonForm({
    mutationDelete,
    editData,
    profileHook,
    resetForm,
    setRefetch,
    handleClose,
  });

  useEffect(() => {
    if (open) {
      if (dialogTitle === "Update" && editData) {
        setFieldValue(
          "contentId.id",
          editData?.contentId?._id || editData?.contentId?.id || "",
        );
        setFieldValue("groupFeeId", {
          id: editData?.groupFeeId?._id,
          title:
            editData?.groupFeeId?.title +
            " : " +
            editData?.groupFeeId?.price +
            " $",
        });
        setFieldValue("title", editData?.title || "");
        // setFieldValue("contentId.title", editData?.contentId?.title || "");
        setFieldValue("numbering", editData?.numbering);
        setFieldValue("description", editData?.description || "");
        setFieldValue("videoLink", editData?.videoLink || "");
      } else {
        resetForm();
      }
    }
  }, [open, editData]);

  return (
    <Dialog
      open={open}
      className="dialog-container"
      maxWidth="md"
      fullWidth
      hideBackdrop={hideBackdrop}
    >
      <DialogTitleComp
        title={
          dialogTitle === "Create"
            ? t("modal_title_create_lesson")
            : t("modal_title_update_lesson")
        }
        classTitle="dialog-title"
        classIcon="close-icon"
        handleClose={handleCloseAction}
      />

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container rowSpacing={2} columnSpacing={3}>
              {/* <Grid item xs={12} sm={7}>
                <Typography className={t("class-name-field-title")}>
                  {t("page_content")}
                </Typography>
                <SelectContent
                  value={values?.contentId}
                  onChange={(val) => {
                    if (val) {
                      setFieldValue("contentId", val);
                    } else {
                      setFieldValue("contentId.id", "");
                      setFieldValue("contentId.title", "");
                    }
                  }}
                />
                {Boolean(errors.contentId?.id && touched.contentId?.id) && (
                  <FormHelperText error id="content-id-error">
                    {errors.contentId?.id}
                  </FormHelperText>
                )}
              </Grid> */}

              <Grid item xs={12} sm={4}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_numbering")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  type="number"
                  fullWidth
                  name="numbering"
                  value={values?.numbering || ""}
                  onChange={handleChange}
                  placeholder={t("thead_numbering") || "Video Link"}
                  error={Boolean(touched.numbering && errors.numbering)}
                  helperText={touched.numbering && errors.numbering}
                />
              </Grid>

              <Grid item xs={12} sm={8}>
                <SelectClassGroupFee
                  value={values?.groupFeeId}
                  onChange={(val) => {
                    setFieldValue(
                      "groupFeeId",
                      val || { id: "", title: "", price: "" },
                    );
                  }}
                />
                {!!errors.groupFeeId?.id && touched.groupFeeId?.id && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors.groupFeeId?.id}
                  </FormHelperText>
                )}
              </Grid>

              {/* Image Preview / Upload */}
              <Grid item xs={12}>
                <Box>
                  <Stack direction="row" justifyContent="center">
                    <Tooltip title="click to upload">
                      <Button component="label">
                        <TextField
                          className="text-field"
                          type="file"
                          id="image"
                          sx={{ display: "none" }}
                          onChange={handleUploadImage}
                        />
                        <img
                          className="class-image"
                          alt=""
                          src={
                            imageFile
                              ? URL.createObjectURL(imageFile)
                              : editData?.image
                                ? editData?.image
                                : EmptyImage
                          }
                        />
                      </Button>
                    </Tooltip>
                  </Stack>
                </Box>

                {statusProgress && progress < 100 ? (
                  <LoadingProgess
                    progress={progress}
                    setProgress={setProgress}
                  />
                ) : (
                  <Typography
                    className={t("class-name-field-title")}
                    sx={{ textAlign: "center", marginTop: "5px" }}
                  >
                    {t("thead_image")}
                  </Typography>
                )}
              </Grid>

              {/* Lesson title */}
              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_title")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  name="title"
                  value={values?.title || ""}
                  onChange={handleChange}
                  placeholder={t("thead_title") || "Video Link"}
                />
              </Grid>

              {/* Video Link */}
              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_video_link")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  name="videoLink"
                  value={values?.videoLink || ""}
                  onChange={handleChange}
                  placeholder={t("thead_video_link") || "Video Link"}
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_description")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  multiline
                  rows={3}
                  fullWidth
                  name="description"
                  value={values?.description || ""}
                  onChange={handleChange}
                  placeholder={t("thead_description") || "Description"}
                />
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                {loading ? (
                  <Button className="btn-create" fullWidth disabled>
                    {t("loading")}
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    type="submit"
                    className="btn-create"
                    disabled={statusProgress && progress < 100 ? true : false}
                  >
                    {dialogTitle === "Update" ? t("update") : t("create")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
